import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField, IconButton, List, ListItem, ListItemText, ListItemButton, Switch, Tooltip, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { EyeExamTemplateService } from '../../../../service/eyeExamTemplateService';
import { useNavigate } from 'react-router-dom';

export default function TemplateDialog({ open, onClose, practitionerId, currentPayload, onApplyTemplate }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await EyeExamTemplateService.list(practitionerId, true);
      setTemplates(data || []);
    } finally { setLoading(false); }
  }, [practitionerId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const resetForm = () => { setEditing(null); setName(''); setDescription(''); setIsPublic(false); };

  const openCreate = () => { resetForm(); setFormOpen(true); };
  const openEdit = (tpl) => { setEditing(tpl); setName(tpl.name || ''); setDescription(tpl.description || ''); setIsPublic((tpl.scope || 'private') === 'public'); setFormOpen(true); };

  const saveTemplate = async () => {
    const record = {
      practitionerId: practitionerId || null,
      name: name.trim() || 'Untitled',
      description: description || '',
      scope: isPublic ? 'public' : 'private',
      payload: currentPayload || {}
    };
    if (editing) {
      await EyeExamTemplateService.update(editing.id, record);
    } else {
      await EyeExamTemplateService.create(record);
    }
    setFormOpen(false);
    await load();
  };

  const deleteTemplate = async (tpl) => {
    await EyeExamTemplateService.delete(tpl.id);
    await load();
  };

  const applyTemplate = async (tpl) => {
    // getById returns the data payload already (not the envelope)
    const result = await EyeExamTemplateService.getById(tpl.id);
    const payload = result?.payload || {};
    onApplyTemplate?.(payload);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Templates {practitionerId}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Tip: You can manage all your templates from the{' '}
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              navigate('/templates');
              onClose?.();
            }}
            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
          >
            Templates page
          </Button>
          {' '}in the main menu.
        </Alert>
        <List dense>
          {(templates || []).map((t) => (
            <ListItem
              key={t.id}
              disableGutters
              secondaryAction={
                <Box>
                  <Tooltip title="Edit">
                    <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openEdit(t); }}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton edge="end" onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemButton onClick={() => applyTemplate(t)} disabled={loading}>
                <ListItemText
                  primary={t.name}
                  secondary={t.description || (t.scope === 'public' ? 'Public' : 'Private')}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Inline Create/Edit Form */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth size="small" />
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
              <span>{isPublic ? 'Public' : 'Private'}</span>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTemplate}>{editing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
